import { motion } from "framer-motion";
import { type FC, useEffect, useRef } from "react";
import { colors } from "widgets/utils";

export const NotFoundPage: FC<{ message: string }> = ({ message }) => {
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      style={{
        width: "100%",
        height: "100%",
        background: "white",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        margin: "auto",
        maxWidth: 640,
      }}
      data-testid="loading-dna"
    >
      <Animation />
      <div
        style={{
          textAlign: "left",
          fontSize: 40,
          fontWeight: 350,
          lineHeight: 0.75,
          margin: 10,
        }}
      >
        Not Found
      </div>
      <pre id="error-message" style={{ color: "var(--primary-main)" }} data-testid="error-message">
        {message}
      </pre>
    </motion.div>
  );
};

const Animation = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current!;
    const ctx = canvas.getContext("2d")!;
    let frame = 0;
    let animationFrameId: number;

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Draw the DNA strands with "errors"
      for (let i = 0; i < canvas.width; i += 4) {
        const baseY = canvas.height / 2;
        const amplitude = 30;
        const frequency = 0.02;
        const speed = 0.05;

        // Create a "mutation" zone in the middle
        const mutationCenter = canvas.width / 2;
        const mutationWidth = 80;
        const inMutationZone = Math.abs(i - mutationCenter) < mutationWidth;

        // Calculate positions with intentional "errors" in the mutation zone
        let y1, y2;
        if (inMutationZone) {
          // Add chaotic movement in mutation zone
          const chaos = Math.sin(frame * 0.1 + i * 0.3) * 15;
          y1 = baseY + Math.sin(i * frequency * 1.5 + frame * speed) * (amplitude + chaos);
          y2 = baseY + Math.sin((i + 100) * frequency * 1.5 + frame * speed) * (amplitude + chaos);
        } else {
          // Normal DNA movement outside mutation zone
          y1 = baseY + Math.sin(i * frequency + frame * speed) * amplitude;
          y2 = baseY + Math.sin((i + 100) * frequency + frame * speed) * amplitude;
        }

        // Draw main strands
        ctx.beginPath();
        ctx.strokeStyle = inMutationZone ? colors.dangerLight : colors.primaryMain;
        ctx.lineWidth = 2;
        ctx.moveTo(i, y1);
        ctx.lineTo(i + 4, y1);
        ctx.stroke();

        ctx.beginPath();
        ctx.strokeStyle = inMutationZone ? colors.dangerMain : colors.successMain;
        ctx.moveTo(i, y2);
        ctx.lineTo(i + 4, y2);
        ctx.stroke();

        // Draw connecting lines with "errors", skip the first one
        if (i % 20 === 0 && i > 0) {
          ctx.beginPath();
          ctx.strokeStyle = inMutationZone
            ? `rgba(255, ${Math.random() * 100}, ${Math.random() * 100}, 0.8)`
            : colors.secondaryLight;
          ctx.lineWidth = inMutationZone ? Math.random() * 2 + 1 : 1;

          if (inMutationZone && Math.random() > 0.5) {
            // Draw "broken" connections in mutation zone
            const midPoint = (y1 + y2) / 2;
            ctx.moveTo(i, y1);
            ctx.lineTo(i + (Math.random() - 0.5) * 10, midPoint);
            ctx.moveTo(i + (Math.random() - 0.5) * 10, midPoint);
            ctx.lineTo(i, y2);
          } else {
            ctx.moveTo(i, y1);
            ctx.lineTo(i, y2);
          }
          ctx.stroke();
        }
      }

      // Draw "404" text integrated into the mutation zone
      ctx.font = "bold 48px monospace";
      ctx.fillStyle = "rgba(255, 75, 75, 0.8)";
      const text = "?";
      const textWidth = ctx.measureText(text).width;
      ctx.fillText(text, canvas.width / 2 - textWidth / 2, canvas.height / 2 + 8);

      frame = frame + 1;
      animationFrameId = requestAnimationFrame(draw);
    };

    draw();

    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} width={400} height={200} />;
};
